// ==================== SMS TEMPLATES CONSTANTS ====================

import type { SmsTemplateCategory } from '../services/sms-templates.service';

// Category options for select dropdowns
export const SMS_TEMPLATE_CATEGORY_OPTIONS: Array<{ value: SmsTemplateCategory; label: string; description: string }> = [
  { 
    value: 'MARKETING', 
    label: 'Marketing', 
    description: 'Promotional messages, offers, and announcements' 
  },
  { 
    value: 'TRANSACTIONAL', 
    label: 'Transactional', 
    description: 'Order confirmations, shipping updates, receipts' 
  },
  { 
    value: 'NOTIFICATION', 
    label: 'Notification', 
    description: 'Alerts, reminders, and updates' 
  },
  { 
    value: 'AUTHENTICATION', 
    label: 'Authentication', 
    description: 'OTP codes, verification messages' 
  },
  { 
    value: 'CUSTOMER_SERVICE', 
    label: 'Customer Service', 
    description: 'Support responses and follow-ups' 
  },
];

// Filter options (includes "All" option)
export const SMS_TEMPLATE_FILTER_CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  ...SMS_TEMPLATE_CATEGORY_OPTIONS,
];

export const SMS_TEMPLATE_FILTER_STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// Character limits
export const SMS_SINGLE_SEGMENT_LIMIT = 160;
export const SMS_MULTI_SEGMENT_LIMIT = 153;
export const SMS_UNICODE_SINGLE_LIMIT = 70;
export const SMS_UNICODE_MULTI_LIMIT = 67;
export const SMS_MAX_CHARACTERS = 1600; // ~10 segments
export const SMS_MAX_SEGMENTS = 10;

// Standard variables available for templates
export const SMS_STANDARD_VARIABLES = [
  { variable: 'name', description: 'Full name', example: 'John Doe' },
  { variable: 'firstName', description: 'First name', example: 'John' },
  { variable: 'lastName', description: 'Last name', example: 'Doe' },
  { variable: 'phone', description: 'Phone number', example: '+1234567890' },
  { variable: 'email', description: 'Email address', example: 'john@example.com' },
  { variable: 'company', description: 'Company name', example: 'Acme Inc' },
  { variable: 'date', description: 'Current date', example: '2026-01-28' },
  { variable: 'time', description: 'Current time', example: '10:30 AM' },
  { variable: 'code', description: 'Verification code', example: '123456' },
  { variable: 'link', description: 'URL link', example: 'https://example.com' },
  { variable: 'amount', description: 'Amount/price', example: '$99.99' },
  { variable: 'orderId', description: 'Order ID', example: 'ORD-12345' },
  { variable: 'custom1', description: 'Custom variable 1', example: 'Value 1' },
  { variable: 'custom2', description: 'Custom variable 2', example: 'Value 2' },
  { variable: 'custom3', description: 'Custom variable 3', example: 'Value 3' },
];

// Helper to calculate segments
export function calculateSmsSegments(content: string): { characterCount: number; segmentCount: number; isUnicode: boolean } {
  const characterCount = content.length;
  const isUnicode = /[^\x00-\x7F]/.test(content);
  
  let segmentCount: number;
  if (isUnicode) {
    if (characterCount <= SMS_UNICODE_SINGLE_LIMIT) {
      segmentCount = 1;
    } else {
      segmentCount = Math.ceil(characterCount / SMS_UNICODE_MULTI_LIMIT);
    }
  } else {
    if (characterCount <= SMS_SINGLE_SEGMENT_LIMIT) {
      segmentCount = 1;
    } else {
      segmentCount = Math.ceil(characterCount / SMS_MULTI_SEGMENT_LIMIT);
    }
  }

  return { characterCount, segmentCount, isUnicode };
}

// Helper to extract variables from content
export function extractSmsVariables(content: string): string[] {
  const variablePattern = /\{\{(\w+)\}\}/g;
  const matches = content.matchAll(variablePattern);
  const variables = new Set<string>();

  for (const match of matches) {
    variables.add(match[1]);
  }

  return Array.from(variables);
}

// Helper to get character limit warning
export function getSmsCharacterWarning(_characterCount: number, segmentCount: number): string | null {
  if (segmentCount > SMS_MAX_SEGMENTS) {
    return `Message exceeds ${SMS_MAX_SEGMENTS} segments. Consider shortening.`;
  }
  if (segmentCount >= 5) {
    return `Message is ${segmentCount} segments. This may increase costs.`;
  }
  if (segmentCount >= 3) {
    return `Message is ${segmentCount} segments.`;
  }
  return null;
}
